import { faCircleCheck, faShare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NotificationDispatch, StyledButton } from '@tryhackme/thm-ui-components';
/* eslint-disable consistent-return */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useMeasure from 'react-use-measure';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setIsRoomComplete } from 'src/app/reducers/room/room.reducer';
import { ToolTip } from 'src/common/components';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import type { RoomTask } from 'src/common/interfaces/tasks';
import { useTrackEventMutation } from 'src/common/slices';
import { useResetUserProgressMutation } from 'src/features/room/room.slice';
import { usePostAnswerMutation } from '../../question-and-answer-item/question-and-answer-item.slice';
import { CompletionShareRoomModal } from '../../room-banner/components/completion-modal-share-roomcompletion-share-room-modal';
import { useHasAnsweredAllQuestionsCorrectly } from '../hooks/use-has-answered-all-questions-correctly';
import { StyledButtonsWrapper, StyledLoading, StyledMessageIcon, StyledMessageText, StyledMessageWrapper, StyledRoomFooterContentWrapper, StyledRoomFooterWrapper, StyledShareButton, StyledTaskProgressStatus, StyledTaskTooltipText, StyledTasksProgressWrapper } from './room-footer.styles';
import { type RoomFooterProps, TaskProgressStatus } from './room-footer.types';
import { useRoomTaskBreakdownContext } from './room-task-breakdown-context';
const MESSAGE_INLINE_MAX_NUMBER_OF_TASKS = 5;
export const RoomFooter = ({
  tasks,
  roomDetails,
  roomCode,
  joinRoom
}: RoomFooterProps) => {
  const isRoomCompleted = useAppSelector(state => state.room.isRoomCompleted);
  const dispatch = useAppDispatch();
  const [ref, {
    width
  }] = useMeasure();
  const isDesktop = width >= BREAKPOINTS.DESKTOP;
  const isMobile = width < BREAKPOINTS.MOBILE;
  const isSmallMobile = width < BREAKPOINTS.SMALL_MOBILE;
  const hasUserJoinedRoom = !!roomDetails?.joined;
  const isDarkMode = useIsDarkMode();
  const [trackEvent] = useTrackEventMutation();
  const navigate = useNavigate();
  const {
    currentTask,
    proceedToTaskNo,
    advanceToNextTask,
    isLastTask,
    firstIncompleteTask,
    completeRoom
  } = useRoomTaskBreakdownContext();
  const doesCurrentTaskRequireAnswer = useMemo(() => {
    if (!currentTask?.questions) {
      return false;
    }
    return !!currentTask.questions?.find(q => !q.progress?.noAnswer);
  }, [currentTask?.questions]);
  const [firstQuestion] = currentTask?.questions || [];
  const [postAnswer, {
    isLoading: isPostAnswerLoading
  }] = usePostAnswerMutation();
  const submitEmptyAnswer = useCallback(async () => {
    try {
      if (!currentTask || !firstQuestion) {
        return;
      }
      await postAnswer({
        answer: '',
        questionNo: firstQuestion.questionNo,
        roomCode,
        taskId: currentTask?._id
      }).unwrap();
      advanceToNextTask();
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
  }, [advanceToNextTask, currentTask, firstQuestion, postAnswer, roomCode]);
  const [, {
    isLoading: isResetRoomProgressLoading,
    isSuccess: resetProgressSuccessful
  }] = useResetUserProgressMutation({
    fixedCacheKey: 'shared-reset-progress'
  });
  const sortedTasks = useMemo(() => [...(tasks || [])].sort((a, b) => a.taskNo - b.taskNo), [tasks]);
  const hasAnsweredAllCurrentTaskQuestionsCorrectly = !!useHasAnsweredAllQuestionsCorrectly(currentTask ? [currentTask] : []);
  const hasAnsweredAllRoomQuestionsCorrectly = !!useHasAnsweredAllQuestionsCorrectly(tasks);
  const numberOfQuestionsAnsweredCorrectly = useMemo(() => currentTask?.questions.filter(q => !!q.progress?.correct).length, [currentTask?.questions]);
  const [showCorrectAnswerMessage, setShowCorrectAnswerMessage] = useState(false);
  const shouldDisplayMessageInline = useMemo(() => {
    const numberOfTasks = tasks?.length ?? 0;
    return isDesktop && numberOfTasks <= MESSAGE_INLINE_MAX_NUMBER_OF_TASKS;
  }, [isDesktop, tasks?.length]);
  const showMessage = hasUserJoinedRoom && (showCorrectAnswerMessage || isRoomCompleted);
  const initialRoomCompletionRef = useRef<boolean>(false);
  useEffect(() => {
    if (tasks && !initialRoomCompletionRef.current) {
      dispatch(setIsRoomComplete(hasAnsweredAllRoomQuestionsCorrectly));
      initialRoomCompletionRef.current = true;
    }
  }, [dispatch, hasAnsweredAllRoomQuestionsCorrectly, isRoomCompleted, tasks]);
  useEffect(() => {
    if (resetProgressSuccessful) {
      dispatch(setIsRoomComplete(false));
    }
  }, [dispatch, resetProgressSuccessful]);
  useEffect(() => {
    if (numberOfQuestionsAnsweredCorrectly) {
      setShowCorrectAnswerMessage(true);
      const timer = setTimeout(() => {
        setShowCorrectAnswerMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [numberOfQuestionsAnsweredCorrectly]);
  useEffect(() => {
    if (currentTask?.taskNo) {
      setShowCorrectAnswerMessage(false);
    }
  }, [currentTask?.taskNo]);
  const {
    nextRoom
  } = roomDetails || {};
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const handleModalOpen = (open: boolean) => {
    setIsShareModalOpen(open);
    if (open) {
      navigate(`?reveal=share`, {
        replace: true
      });
    } else {
      navigate('', {
        replace: true
      });
    }
  };
  const handleGoToNextRoom = async () => {
    if (!nextRoom) {
      return;
    }
    // eslint-disable-next-line no-void
    await trackEvent({
      event: 'click-next-room-button',
      properties: {
        current_room: roomCode,
        next_room: nextRoom.code,
        clicked_on: 'from_footer'
      }
    });
    navigate(`/jr/${nextRoom.code}`);
  };
  const getTaskStatus = (task: RoomTask) => {
    const isCompleted = task.questions.every(q => !!q.progress?.correct);
    if (isCompleted) {
      return TaskProgressStatus.COMPLETED;
    }
    const isInProgress = task.taskNo === firstIncompleteTask?.taskNo;
    if (isInProgress) {
      return TaskProgressStatus.IN_PROGRESS;
    }
    return TaskProgressStatus.NOT_STARTED;
  };
  const renderMessage = () => {
    if (!showMessage) {
      return null;
    }
    return <StyledMessageWrapper $isCorrectMessage={showCorrectAnswerMessage} data-sentry-element="StyledMessageWrapper" data-sentry-component="renderMessage" data-sentry-source-file="room-footer.tsx">
        <StyledMessageIcon icon={faCircleCheck} $isDarkMode={isDarkMode} data-sentry-element="StyledMessageIcon" data-sentry-source-file="room-footer.tsx" />
        <StyledMessageText $isDarkMode={isDarkMode} data-sentry-element="StyledMessageText" data-sentry-source-file="room-footer.tsx">
          {isRoomCompleted ? 'Room completed' : 'Woop woop! Your answer is correct.'}
        </StyledMessageText>
      </StyledMessageWrapper>;
  };
  const renderSubmitButton = () => {
    const handleClick = async () => {
      if (!hasUserJoinedRoom) {
        await joinRoom();
        return;
      }
      if (!doesCurrentTaskRequireAnswer) {
        await submitEmptyAnswer();
      }
      if (isLastTask) {
        completeRoom();
      } else {
        advanceToNextTask();
      }
    };
    const getButtonLabel = () => {
      if (!hasUserJoinedRoom) {
        return 'Join Room';
      }
      return isLastTask ? 'Complete room' : 'Continue';
    };
    return <ToolTip sideOffset={0} toolTipText="All questions must be answered before moving to the next task." $contentMaxWidth="24rem" disabled={!hasUserJoinedRoom || hasAnsweredAllCurrentTaskQuestionsCorrectly || !doesCurrentTaskRequireAnswer} data-sentry-element="ToolTip" data-sentry-component="renderSubmitButton" data-sentry-source-file="room-footer.tsx">
        <StyledButton variant="solid" type="submit" color="primary" disabled={hasUserJoinedRoom && (isPostAnswerLoading || !hasAnsweredAllCurrentTaskQuestionsCorrectly && doesCurrentTaskRequireAnswer)}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={handleClick} data-sentry-element="StyledButton" data-sentry-source-file="room-footer.tsx">
          {getButtonLabel()}
        </StyledButton>
      </ToolTip>;
  };
  const renderButtons = () => <StyledButtonsWrapper $isSmallMobile={isSmallMobile} data-sentry-element="StyledButtonsWrapper" data-sentry-component="renderButtons" data-sentry-source-file="room-footer.tsx">
      {isRoomCompleted && hasUserJoinedRoom ? <>
          <CompletionShareRoomModal roomCode={roomDetails?.code ?? ''} roomType={roomDetails?.type ?? 'challenge'} isOpenModal={isShareModalOpen} isShareModalOpen={isShareModalOpen} onOpenChange={handleModalOpen} setIsShareModalOpen={setIsShareModalOpen} triggerElement={<StyledShareButton data-testid="share-achievement-button" color={nextRoom ? 'secondary' : 'primary'} $isSecondary={!!nextRoom} $isDark={isDarkMode}>
                <FontAwesomeIcon icon={faShare} />
                Share your achievement
              </StyledShareButton>} />
          {nextRoom && <StyledButton
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={handleGoToNextRoom} color="primary" variant="solid" data-testid="next-room-button">
              Go to next room
            </StyledButton>}
        </> : renderSubmitButton()}
    </StyledButtonsWrapper>;
  const isLoading = isResetRoomProgressLoading || !roomDetails || !tasks;
  return <StyledRoomFooterWrapper ref={ref} $showMessage={showMessage} $isDesktop={isDesktop} $isMobile={isMobile} $isDarkMode={isDarkMode} data-testid="room-footer" data-sentry-element="StyledRoomFooterWrapper" data-sentry-component="RoomFooter" data-sentry-source-file="room-footer.tsx">
      {!shouldDisplayMessageInline && renderMessage()}
      <StyledRoomFooterContentWrapper $isMobile={isMobile} data-sentry-element="StyledRoomFooterContentWrapper" data-sentry-source-file="room-footer.tsx">
        {isLoading ? <StyledLoading /> : <>
            <StyledTasksProgressWrapper data-testid="task-progress">
              {sortedTasks.map(task => {
            const taskStatus = getTaskStatus(task);
            return <ToolTip key={task._id} sideOffset={0} toolTipText={<StyledTaskTooltipText data-testid={`task-tooltip-${task.taskNo}`} data-task-title={task.title}>
                        <span>Task {task.taskNo}</span>
                        <span>{task.title}</span>
                      </StyledTaskTooltipText>}>
                    <StyledTaskProgressStatus $isDark={isDarkMode} $isCurrentTask={task.taskNo === currentTask?.taskNo} $isDisabled={!!firstIncompleteTask && task.taskNo > firstIncompleteTask?.taskNo} key={task._id} $status={taskStatus} data-state={taskStatus} data-testid={`task-progress-item-${task.taskNo}`} onClick={() => {
                if (firstIncompleteTask && task.taskNo <= firstIncompleteTask.taskNo) {
                  proceedToTaskNo(task.taskNo);
                }
              }} />
                  </ToolTip>;
          })}
            </StyledTasksProgressWrapper>
            {shouldDisplayMessageInline && renderMessage()}
            {renderButtons()}
          </>}
      </StyledRoomFooterContentWrapper>
    </StyledRoomFooterWrapper>;
};